/**
 * Joko: Cash back & automatic coupons — shopping rewards helper
 *
 * Declares a single web-accessible-resource group matched to every origin with no
 * `use_dynamic_url`, globbing `assets/*` — which covers the vendor logos
 * (`logo_128.png`, `logo-round.svg`) as well as the coupon scripts and fonts. Every
 * bundled asset resolves from any page at a fixed URL.
 *
 * The 128px logo is the primary signal — an image, so it loads through `probeImage`
 * with no CORS grant and survives a strict page CSP. The round logo and a coupon script
 * corroborate.
 *
 * Manifest verified against version 2.1.120.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'jigflhhckdjdefdjmodlkomnmdonfbbn';

export const joko: ExtensionDefinition = {
  id: ID,
  name: 'Joko: Cash back & automatic coupons',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'Whole assets/ tree exposed to every origin with no dynamic URL. Loading logo_128.png ' +
    'is conclusive and needs no CORS grant; the round logo and a coupon script corroborate.',
  signals: [
    {
      id: 'war-logo-128-png',
      kind: 'web-accessible-resource',
      description:
        'Load assets/logo_128.png via <img> — a vendor logo exposed to every origin; ' +
        'needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/logo_128.png'),
    },
    {
      id: 'war-logo-round-svg',
      kind: 'web-accessible-resource',
      description: 'Load assets/logo-round.svg via <img> — a second vendor logo, corroborating.',
      weight: 0.5,
      run: () => probeImage(ID, 'assets/logo-round.svg'),
    },
    {
      id: 'war-capture-headers-coupon-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch assets/captureHeadersAuthorizationTokenCouponScript.js — a vendor-named ' +
        'coupon script, an independent non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'assets/captureHeadersAuthorizationTokenCouponScript.js'),
    },
  ],
};
